/**
 * PracticeBanner — "Viewing: ABC Pvt Ltd" persistent top bar
 *
 * Shown when a CA enters a client's org via "Enter Client".
 * Provides immediate context and a quick "Exit" button to return
 * to the CA's own practice org.
 *
 * Renders ABOVE the header — designed to be visually prominent
 * with a teal accent so the CA never forgets they're in a client context.
 *
 * @since 2.0.0
 */
import { useAuth } from '@/shared/hooks/useAuth';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Building2, Eye, Shield } from 'lucide-react';
import { OFFICE_ROUTE } from '@/shared/config/officeIdentity';

export function PracticeBanner() {
    const { viewingAsClient, viewingClientName, currentOrg, exitClientOrg } = useAuth();
    const navigate = useNavigate();

    if (!viewingAsClient || !viewingClientName) return null;

    // Out through the SAME door as the header's Office pill (office-boundary).
    // This button used to land on `/practice` — a different, older screen — so
    // "Back to Office" and "Office" meant two places.
    const handleExit = () => {
        exitClientOrg();
        navigate({ to: OFFICE_ROUTE });
    };

    return (
        <div className="relative z-50 flex items-center justify-between px-4 py-1.5 bg-teal-700 text-white">
            {/* Left: Context */}
            <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-teal-100">
                    <Eye size={13} />
                    <span className="text-[11px] font-medium uppercase tracking-wider">Viewing Client</span>
                </div>
                <div className="w-px h-4 bg-white/20" />
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-white/15 flex items-center justify-center">
                        <Building2 size={12} />
                    </div>
                    <span className="text-sm font-semibold tracking-tight">{viewingClientName}</span>
                    {currentOrg?.org_type && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-teal-100">
                            {currentOrg.org_type.replace('in_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                    )}
                </div>
            </div>

            {/* Center: Permission badge */}
            <div className="hidden sm:flex items-center gap-1.5 text-teal-100 text-[11px]">
                <Shield size={11} />
                <span>Accountant Access</span>
            </div>

            {/* Right: Exit button */}
            <button
                onClick={handleExit}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-lg transition-all active:scale-95"
            >
                <ArrowLeft size={13} />
                {/* "Office" is the product's word for the firm altitude (cc-11). */}
                <span className="hidden sm:inline">Back to Office</span>
                <span className="sm:hidden">Exit</span>
            </button>
        </div>
    );
}
